import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import MainURL from '../../MainURL';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { recoilLoginState } from '../../RecoilStore';
import { useDropzone } from 'react-dropzone';
import imageCompression from "browser-image-compression";
import { format } from "date-fns";
import Loading from '../../components/Loading';
import { Icon, PageHead } from '../AdminLayout';
import defaultMainImage from '../../images/mainimage.jpg';

interface MainDataProps {
  id: string;
  name: string;
  charger: string;
  address: string;
  youtube: string;
  blog: string;
  facebook: string;
  instar: string;
  quiry: string;
  supportAccount: string;
  imageMainName: string;
  imageText: string;
  mainLogo: string;
  mainLogoMini: string;
  mainMessage: string;
  mainService: string;
  facility: string;
  placeNaver: string;
  placeKakao: string;
}

export default function EditMainInfo() {
  const navigate = useNavigate();
  const isLogin = useRecoilValue(recoilLoginState);

  const [form, setForm] = useState<MainDataProps | null>(null);
  const [mainMessageList, setMainMessageList] = useState<any[]>([]);
  const [mainServiceList, setMainServiceList] = useState<any[]>([]);
  const [facilityList, setFacilityList] = useState<any[]>([]);
  const [greeting, setGreeting] = useState<{ image?: string; fromname?: string; content?: string[] }>({ image: '', fromname: '', content: [''] });
  const [currentTab, setCurrentTab] = useState<number>(1);

  // 이미지 첨부 관련 상태
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  const [greetingImageFiles, setGreetingImageFiles] = useState<File[]>([]);
  const [serviceImageFiles, setServiceImageFiles] = useState<{ [key: number]: File[] }>({});
  const [facilityImageFiles, setFacilityImageFiles] = useState<{ [key: number]: File[] }>({});
  const [currentServiceIndex, setCurrentServiceIndex] = useState<number>(-1);
  const [currentFacilityIndex, setCurrentFacilityIndex] = useState<number>(-1);
  
  // 개별 서비스 항목 수정을 위한 상태
  const [editingServiceIndex, setEditingServiceIndex] = useState<number>(-1);
  const [editingService, setEditingService] = useState<any>(null);
  
  // 개별 시설 항목 수정을 위한 상태
  const [editingFacilityIndex, setEditingFacilityIndex] = useState<number>(-1);
  const [editingFacility, setEditingFacility] = useState<any>(null);

  // 이미지 첨부 함수
  const currentDate = new Date();
  const date = format(currentDate, 'yyyy-MM-dd');
  
  // 임의의 영문+숫자 문자열 생성 함수
  const generateRandomString = (length: number): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  // 이미지 압축 및 파일명 생성 함수 (jpg/jpeg/png만 허용)
  const processImageFiles = async (acceptedFiles: File[]) => {
    try {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      const validFiles = acceptedFiles.filter(f => allowedTypes.includes(f.type));
      if (validFiles.length === 0) {
        alert('jpg, jpeg, png 형식의 이미지 파일만 업로드할 수 있습니다.');
        return [];
      }

      const options = {
        maxSizeMB: 1,
        maxWidthOrHeight: 1000
      };
      const resizedFiles = await Promise.all(
        validFiles.map(async (file) => {
          setImageLoading(true);
          const resizingBlob = await imageCompression(file, options);
          return resizingBlob;
        })
      );
      
      const adminId = 'admin'; // 관리자 ID
      const fileCopies = resizedFiles.map((resizedFile, index) => {
        const randomString = generateRandomString(10);
        const originalFile = validFiles[index];
        const mime = originalFile.type || '';
        const extension = mime.includes('png')
          ? '.png'
          : mime.includes('jpeg') || mime.includes('jpg')
            ? '.jpg'
            : '.jpg';
        return new File([resizedFile], `${date}${adminId}_${randomString}${extension}`, {
          type: originalFile.type,
        });
      });
      setImageLoading(false);
      return fileCopies;
    } catch (error) {
      console.error('이미지 리사이징 중 오류 발생:', error);
      setImageLoading(false);
      return [];
    }
  };

  // 각 탭별 드롭존 설정
  const greetingDropzone = useDropzone({ 
    onDrop: useCallback(async (acceptedFiles: File[]) => {
      const processedFiles = await processImageFiles(acceptedFiles);
      setGreetingImageFiles(processedFiles);
    }, []) 
  });
  
  const serviceDropzone = useDropzone({ 
    onDrop: useCallback(async (acceptedFiles: File[]) => {
      console.log('서비스 드롭존 onDrop 호출됨, 파일 수:', acceptedFiles.length);
      console.log('현재 서비스 인덱스:', currentServiceIndex);
      const processedFiles = await processImageFiles(acceptedFiles);
      // 현재 선택된 서비스 인덱스가 있으면 해당 인덱스에, 없으면 첫 번째 인덱스에 저장
      const targetIndex = currentServiceIndex >= 0 ? currentServiceIndex : 0;
      console.log('타겟 인덱스:', targetIndex);
      setServiceImageFiles(prev => ({
        ...prev,
        [targetIndex]: processedFiles
      }));
      // 인덱스 초기화
      setCurrentServiceIndex(-1);
    }, [currentServiceIndex]) 
  });
  
  const facilityDropzone = useDropzone({ 
    onDrop: useCallback(async (acceptedFiles: File[]) => {
      const processedFiles = await processImageFiles(acceptedFiles);
      // 현재 선택된 시설 인덱스가 있으면 해당 인덱스에, 없으면 첫 번째 인덱스에 저장
      const targetIndex = currentFacilityIndex >= 0 ? currentFacilityIndex : 0;
      setFacilityImageFiles(prev => ({
        ...prev,
        [targetIndex]: processedFiles
      }));
      // 인덱스 초기화
      setCurrentFacilityIndex(-1);
    }, [currentFacilityIndex]) 
  });

  // 메인 배경 이미지: 화면 전체 폭에 쓰이므로 더 큰 해상도로 압축 후 바로 업로드
  const mainBgDropzone = useDropzone({
    onDrop: useCallback(async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (!file) return;
      if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
        alert('jpg, jpeg, png 형식의 이미지 파일만 업로드할 수 있습니다.');
        return;
      }
      setImageLoading(true);
      try {
        const resized = await imageCompression(file, { maxSizeMB: 2, maxWidthOrHeight: 1920 });
        const extension = file.type.includes('png') ? '.png' : '.jpg';
        const renamed = new File([resized], `${date}admin_main_${generateRandomString(10)}${extension}`, { type: file.type });
        const fd = new FormData();
        fd.append('img', renamed);
        const res = await axios.post(`${MainURL}/main/upload/mainimage`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (res.data && res.data.filename) {
          setForm(prev => prev ? { ...prev, imageMainName: res.data.filename } : prev);
        } else {
          alert('이미지 업로드에 실패했습니다.');
        }
      } catch (error) {
        console.error('메인 배경 이미지 업로드 오류:', error);
        alert('이미지 업로드 중 오류가 발생했습니다.');
      } finally {
        setImageLoading(false);
      }
    }, []),
    multiple: false
  });

  // 첨부 이미지 삭제 함수들
  const deleteGreetingImage = (idx: number) => {
    const copy = [...greetingImageFiles];
    const newItems = copy.filter((item, i) => i !== idx);
    setGreetingImageFiles(newItems);
  };

  const deleteServiceImage = (serviceIndex: number, fileIndex: number) => {
    setServiceImageFiles(prev => {
      const currentFiles = prev[serviceIndex] || [];
      const newFiles = currentFiles.filter((_, i) => i !== fileIndex);
      return {
        ...prev,
        [serviceIndex]: newFiles
      };
    });
  };

  const deleteFacilityImage = (facilityIndex: number, fileIndex: number) => {
    setFacilityImageFiles(prev => {
      const currentFiles = prev[facilityIndex] || [];
      const newFiles = currentFiles.filter((_, i) => i !== fileIndex);
      return {
        ...prev,
        [facilityIndex]: newFiles
      };
    });
  };

  // 개별 서비스 항목 수정 함수들
  const startEditService = (serviceIndex: number) => {
    setEditingServiceIndex(serviceIndex);
    setEditingService({ ...mainServiceList[serviceIndex] });
    setServiceImageFiles({ [serviceIndex]: [] });
  };

  const cancelEditService = () => {
    setEditingServiceIndex(-1);
    setEditingService(null);
    setServiceImageFiles({});
  };

  // 개별 시설 항목 수정 함수들
  const startEditFacility = (facilityIndex: number) => {
    setEditingFacilityIndex(facilityIndex);
    setEditingFacility({ ...facilityList[facilityIndex] });
    setFacilityImageFiles({ [facilityIndex]: [] });
  };

  const cancelEditFacility = () => {
    setEditingFacilityIndex(-1);
    setEditingFacility(null);
    setFacilityImageFiles({});
  };

  const saveFacilityItem = async (facilityIndex: number) => {
    if (!editingFacility) return;

    try {
      // 이미지가 첨부된 경우 업로드
      let imageFilename = editingFacility.image;
      if (facilityImageFiles[facilityIndex] && facilityImageFiles[facilityIndex].length > 0) {
        const uploadedFilename = await uploadImage(facilityImageFiles[facilityIndex][0]);
        if (uploadedFilename) {
          imageFilename = uploadedFilename;
        }
      }

      // 로컬 상태 업데이트
      const updatedFacilityList = facilityList.map((item, i) => 
        i === facilityIndex ? { ...editingFacility, image: imageFilename } : item
      );
      setFacilityList(updatedFacilityList);

      // 수정 모드 종료
      setEditingFacilityIndex(-1);
      setEditingFacility(null);
      setFacilityImageFiles({});

      alert('시설 항목이 저장되었습니다. (저장 버튼을 눌러 전체 반영)');
    } catch (error) {
      console.error('시설 항목 저장 오류:', error);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  const saveServiceItem = async (serviceIndex: number) => {
    if (!editingService) return;
    
    try {
      // 이미지가 첨부된 경우 업로드
      let imageFilename = editingService.image;
      if (serviceImageFiles[serviceIndex] && serviceImageFiles[serviceIndex].length > 0) {
        const uploadedFilename = await uploadImage(serviceImageFiles[serviceIndex][0]);
        if (uploadedFilename) {
          imageFilename = uploadedFilename;
        }
      }

      // 로컬 상태 업데이트
      const updatedServiceList = mainServiceList.map((item, i) => 
        i === serviceIndex ? { ...editingService, image: imageFilename } : item
      );
      setMainServiceList(updatedServiceList);
      
      // 수정 모드 종료
      setEditingServiceIndex(-1);
      setEditingService(null);
      setServiceImageFiles({});
      
      alert('서비스 항목이 저장되었습니다. (저장 버튼을 눌러 전체 반영)');
    } catch (error) {
      console.error('서비스 항목 저장 오류:', error);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  // 이미지 업로드 함수
  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      console.log('이미지 업로드 시작:', file.name, file.size, file.type);
      const fd = new FormData();
      fd.append('img', file);
      
      console.log('FormData 생성됨, 업로드 URL:', `${MainURL}/main/upload/notice`);
      
      const res = await axios.post(`${MainURL}/main/upload/notice`, fd, { 
        headers: { 'Content-Type': 'multipart/form-data' } 
      });
      
      console.log('서버 응답:', res.data);
      
      if (res.data && res.data.filename) {
        console.log('업로드 성공, 파일명:', res.data.filename);
        return res.data.filename;
      } else {
        console.error('서버 응답에 filename이 없음:', res.data);
        return null;
      }
    } catch (error: any) {
      console.error('이미지 업로드 실패:', error);
      if (error.response) {
        console.error('서버 응답 오류:', error.response.data);
        console.error('HTTP 상태:', error.response.status);
      }
      return null;
    }
  };

  const fetchMain = async () => {
    const res = await axios.get(`${MainURL}/main/getmaininfo`);
    if (res.data && res.data.length > 0) {
      const base = res.data[0];
      setForm(base);
      try { setGreeting(base.greeting ? JSON.parse(base.greeting) : { image: '', fromname: '', content: [''] }); } catch { setGreeting({ image: '', fromname: '', content: [''] }); }
      try { setMainMessageList(base.mainMessage ? JSON.parse(base.mainMessage) : []); } catch { setMainMessageList([]); }
    }
    // mainServiceImage / mainFacilityImage는 별도 테이블에서 조회
    try {
      const svcRes = await axios.get(`${MainURL}/main/getmainserviceimage`);
      if (svcRes.data && Array.isArray(svcRes.data)) {
        const parsed = svcRes.data.map((item:any)=> ({
          ...item,
          content: (() => { try { return Array.isArray(item.content) ? item.content : JSON.parse(item.content || '[]'); } catch { return []; } })()
        }));
        setMainServiceList(parsed);
      } else {
        setMainServiceList([]);
      }
    } catch (e) {
      console.error('mainServiceImage 로드 오류:', e);
      setMainServiceList([]);
    }
    try {
      const facRes = await axios.get(`${MainURL}/main/getmainfacilityimage`);
      if (facRes.data && Array.isArray(facRes.data)) {
        setFacilityList(facRes.data);
      } else {
        setFacilityList([]);
      }
    } catch (e) {
      console.error('mainFacilityImage 로드 오류:', e);
      setFacilityList([]);
    }
  };

  useEffect(() => {
    if (!isLogin) {
      alert('로그인이 필요합니다.');
      navigate('/admin');
      return;
    }
    fetchMain();
  }, []);

  const updateField = (key: keyof MainDataProps, value: string) => {
    if (!form) return;
    setForm({ ...form, [key]: value });
  };

  const [saving, setSaving] = useState<boolean>(false);
  const save = async () => {
    if (!form || saving) return;
    setSaving(true);
    try {
      // mainServiceImage 저장
      const svcRes = await axios.post(`${MainURL}/main/mainserviceimage/save`, { items: mainServiceList });
      if (!svcRes.data || !svcRes.data.success) {
        alert('서비스 이미지 저장에 실패했습니다.');
        return;
      }
      // mainFacilityImage 저장
      const facRes = await axios.post(`${MainURL}/main/mainfacilityimage/save`, { items: facilityList });
      if (!facRes.data || !facRes.data.success) {
        alert('시설 이미지 저장에 실패했습니다.');
        return;
      }

      const payload = {
        ...form,
        greeting: JSON.stringify(greeting || {}),
        mainMessage: JSON.stringify(mainMessageList || []),
        // mainService / facility는 별도 테이블로 관리
      };
      const res = await axios.post(`${MainURL}/main/updatemaininfo`, payload);
      if (res.data === true) {
        alert('저장되었습니다.');
        navigate('/admin/main');
      } else {
        alert('저장에 실패했습니다.');
      }
    } catch (err:any) {
      console.error('저장 오류:', err);
      alert(err.response?.data?.message || '저장 중 오류가 발생했습니다.');
    } finally {
      setSaving(false);
    }
  };

  // 서버에 올라간 기존 이미지 삭제 (서비스/시설 수정 모드 공용)
  const deleteServerImage = async (filename: string, onDeleted: () => void) => {
    if (!window.confirm('이 이미지를 삭제하시겠습니까?')) return;
    try {
      const res = await axios.post(`${MainURL}/main/deleteimage`, { filename });
      if (res.data === true) {
        onDeleted();
        alert('이미지가 삭제되었습니다.');
      } else {
        alert('이미지 삭제에 실패했습니다.');
      }
    } catch (error) {
      console.error('이미지 삭제 오류:', error);
      alert('이미지 삭제 중 오류가 발생했습니다.');
    }
  };

  const TABS = [
    { id: 1, label: '인사말', desc: '작성자와 문장을 수정할 수 있습니다.' },
    { id: 6, label: '기관 소개', desc: '미션 · 비전 · 운영목적 등 항목별 문장을 관리합니다.' },
    { id: 2, label: '제공 서비스', desc: '서비스별 제목 · 내용 · 이미지를 관리합니다.' },
    { id: 3, label: '시설 사진', desc: '시설명과 사진을 관리합니다.' },
    { id: 4, label: '시설 정보', desc: '기관 기본 정보와 SNS · 연락처를 수정합니다.' },
    { id: 5, label: '오시는 길', desc: '지도 링크와 주소를 수정합니다.' },
    { id: 7, label: '메인 배경', desc: '홈페이지 첫 화면 배경 이미지를 변경합니다.' },
  ];
  const activeTab = TABS.find((t) => t.id === currentTab) || TABS[0];

  // 이미지 첨부 버튼 + 첨부 파일 미리보기 (서비스/시설 수정 모드 공용)
  const renderItemImage = (
    label: string,
    image: string,
    onRemoveServer: () => void,
    dropzone: typeof serviceDropzone,
    onPick: () => void,
    files: File[],
    onRemoveFile: (fileIndex: number) => void,
  ) => (
    <div className="ad-field">
      <label>{label}</label>
      <div className="ad-upload">
        {image
          ? <img className="ad-thumb ad-thumb--md" src={`${MainURL}/images/notice/${image}`} alt="" />
          : <span className="ad-thumb ad-thumb--md ad-thumb--empty"><Icon name="image" /></span>}
        <div style={{ display:'grid', gap: 8, justifyItems: 'start' }}>
          {imageLoading ? (
            <div className="ad-loading"><Loading/></div>
          ) : (
            <div onClick={onPick}>
              <div {...dropzone.getRootProps()}>
                <input {...dropzone.getInputProps()} />
                <span className="ad-drop"><Icon name="image" />{files.length > 0 ? '다시 첨부하기' : '새 이미지 첨부'}</span>
              </div>
            </div>
          )}
          {image && (
            <button type="button" className="ad-btn ad-btn--ghost ad-btn--sm is-danger" onClick={onRemoveServer}>
              기존 이미지 삭제
            </button>
          )}
        </div>
      </div>
      {files.length > 0 && (
        <div className="ad-files">
          {files.map((item: File, index: number) => (
            <div key={index} className="ad-file">
              <img src={URL.createObjectURL(item)} alt="" />
              <span>{item.name}</span>
              <button type="button" className="ad-btn ad-btn--ghost ad-btn--icon is-danger" aria-label="첨부 취소" onClick={() => onRemoveFile(index)}>
                <Icon name="close" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const textField = (label: string, key: keyof MainDataProps, placeholder?: string) => (
    <div className="ad-field">
      <label htmlFor={`mi-${key}`}>{label}</label>
      <input id={`mi-${key}`} className="ad-input" placeholder={placeholder} value={form?.[key] || ''} onChange={(e)=>updateField(key, e.target.value)} />
    </div>
  );

  if (!form) return (
    <>
      <PageHead title="사이트 정보 편집" />
      <div className="ad-card ad-card__pad"><div className="ad-loading"><Loading/></div></div>
    </>
  );

  return (
    <>
      <PageHead title="사이트 정보 편집"
        desc="메인 페이지와 하위 페이지에 표시되는 내용을 수정합니다. 항목을 바꾼 뒤 저장하면 홈페이지에 반영됩니다.">
        <a className="ad-btn" href="/" target="_blank" rel="noopener noreferrer">
          <Icon name="external" />
          사이트에서 확인
        </a>
      </PageHead>

      <div className="ad-tabs" role="tablist">
        {TABS.map((t) => (
          <button type="button" role="tab" key={t.id} className={`ad-tab ${currentTab === t.id ? 'is-on' : ''}`} onClick={()=>setCurrentTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="ad-card">
        <div className="ad-card__head">
          <h2>{activeTab.label}</h2>
          <p>{activeTab.desc}</p>
        </div>

        <div className="ad-card__pad">

          {/* 메인 배경 */}
          {currentTab === 7 && (
            <div className="ad-field">
              <label>홈페이지 첫 화면 배경 이미지</label>
              <img
                className="ad-thumb ad-thumb--lg"
                src={form.imageMainName ? `${MainURL}/images/mainimages/${form.imageMainName}` : defaultMainImage}
                onError={(e)=> { e.currentTarget.src = defaultMainImage; }}
                alt=""
              />
              <p className="ad-hint">{form.imageMainName ? form.imageMainName : '기본 이미지 사용 중'}</p>
              {imageLoading ? (
                <div className="ad-loading"><Loading/></div>
              ) : (
                <div className="ad-upload" style={{ marginTop: 4 }}>
                  <div {...mainBgDropzone.getRootProps()}>
                    <input {...mainBgDropzone.getInputProps()} />
                    <span className="ad-drop"><Icon name="image" />배경 이미지 변경</span>
                  </div>
                  {form.imageMainName && (
                    <button type="button" className="ad-btn ad-btn--sm"
                      onClick={()=> {
                        if (window.confirm('기본 배경 이미지로 되돌리시겠습니까?')) updateField('imageMainName', '');
                      }}>
                      기본 이미지로 되돌리기
                    </button>
                  )}
                </div>
              )}
              <p className="ad-hint">가로가 긴 이미지(1920px 이상 권장)를 사용하세요. 변경 후 하단의 저장 버튼을 눌러야 홈페이지에 반영됩니다.</p>
            </div>
          )}

          {/* 인사말 */}
          {currentTab === 1 && (
            <>
              <div className="ad-field">
                <label htmlFor="greetingFrom">작성자</label>
                <input id="greetingFrom" className="ad-input" placeholder="작성자를 입력하세요" value={greeting.fromname || ''} onChange={(e)=> setGreeting({ ...greeting, fromname: e.target.value })} />
                <p className="ad-hint">홈페이지 인사말 영역에 이름으로 표시됩니다.</p>
              </div>
              <div className="ad-field">
                <label>내용 <em>{(greeting.content || []).length}문장</em></label>
                <div className="ad-rep">
                  {(greeting.content || []).map((line:string, idx:number)=> (
                    <div key={idx} className="ad-rep__item">
                      <div className="ad-rep__top">
                        <span className="ad-rep__no">{idx + 1}</span>
                        <span>문장 {idx + 1}</span>
                        <button type="button" className="ad-rep__del"
                          onClick={()=> setGreeting({ ...greeting, content: (greeting.content || []).filter((_, i)=> i!==idx) })}>
                          이 문장 삭제
                        </button>
                      </div>
                      <textarea
                        className='ad-input'
                        rows={4}
                        placeholder="내용을 입력하세요"
                        value={line}
                        onChange={(e)=>{
                          const c = (greeting.content || []).map((t, i)=> i===idx ? e.target.value : t);
                          setGreeting({ ...greeting, content: c });
                        }}
                      />
                    </div>
                  ))}
                </div>
                <div className="ad-rep__foot">
                  <button type="button" className="ad-btn ad-btn--primary ad-btn--sm" onClick={()=> setGreeting({ ...greeting, content: [ ...(greeting.content || []), '' ] })}>
                    <Icon name="plus" />문장 추가
                  </button>
                  <button type="button" className="ad-btn ad-btn--danger ad-btn--sm" onClick={()=> setGreeting({ ...greeting, content: (greeting.content || []).slice(0, -1) })}>
                    <Icon name="minus" />마지막 삭제
                  </button>
                </div>
              </div>
            </>
          )}

          {/* 기관 소개 */}
          {currentTab === 6 && (
            <div className="ad-field">
              <label>기관 소개 항목 <em>{mainMessageList.length}개</em></label>
              <div className="ad-rep">
                {mainMessageList.map((msg:any, mi:number)=> (
                  <div key={mi} className="ad-rep__item">
                    <div className="ad-rep__top">
                      <span className="ad-rep__no">{mi + 1}</span>
                      <input
                        className="ad-input"
                        placeholder="항목 제목 (예: 미션, 비전, 운영목적)"
                        value={msg.title || ''}
                        onChange={(e)=> setMainMessageList(mainMessageList.map((m, i)=> i===mi ? { ...m, title: e.target.value } : m))}
                      />
                      <button type="button" className="ad-rep__del" style={{ marginLeft: 0 }}
                        onClick={()=> {
                          if (window.confirm(`<${msg.title || '항목'}> 항목을 삭제하시겠습니까?`)) {
                            setMainMessageList(mainMessageList.filter((_:any, i:number)=> i!==mi));
                          }
                        }}>
                        항목 삭제
                      </button>
                    </div>
                    {(msg.content || []).map((line:string, li:number)=> (
                      <div key={li} className="ad-rep__line">
                        <textarea
                          className='ad-input'
                          placeholder="내용"
                          rows={3}
                          style={{ minHeight: 76 }}
                          value={line}
                          onChange={(e)=> {
                            const newContent = (msg.content || []).map((x:string, xi:number)=> xi===li ? e.target.value : x);
                            setMainMessageList(mainMessageList.map((m, i)=> i===mi ? { ...m, content: newContent } : m));
                          }}
                        />
                        <button type="button" className="ad-btn ad-btn--ghost ad-btn--icon is-danger" aria-label="문장 삭제"
                          onClick={()=> {
                            const newContent = (msg.content || []).filter((_:string, xi:number)=> xi!==li);
                            setMainMessageList(mainMessageList.map((m, i)=> i===mi ? { ...m, content: newContent } : m));
                          }}>
                          <Icon name="trash" />
                        </button>
                      </div>
                    ))}
                    <button type="button" className="ad-btn ad-btn--sm" style={{ marginTop: 4 }}
                      onClick={()=> setMainMessageList(mainMessageList.map((m, i)=> i===mi ? { ...m, content: [...(m.content || []), ''] } : m))}>
                      <Icon name="plus" />문장 추가
                    </button>
                  </div>
                ))}
              </div>
              <div className="ad-rep__foot">
                <button type="button" className="ad-btn ad-btn--primary ad-btn--sm"
                  onClick={()=> setMainMessageList([ ...mainMessageList, { title: '', content: [''] } ])}>
                  <Icon name="plus" />항목 추가
                </button>
              </div>
            </div>
          )}

          {/* 제공 서비스 */}
          {currentTab === 2 && (
            <div className="ad-items">
              {mainServiceList.map((svc:any, si:number)=> (
                <div key={si} className={`ad-item ${editingServiceIndex === si ? 'is-editing' : ''}`}>
                  <div className="ad-item__row" style={{ alignItems: 'flex-start' }}>
                    {svc.image
                      ? <img className="ad-thumb" src={`${MainURL}/images/notice/${svc.image}`} alt="" />
                      : <span className="ad-thumb ad-thumb--empty"><Icon name="image" /></span>}
                    <div className="ad-item__body">
                      <h3>{svc.title || '제목 없음'}</h3>
                      {(svc.content || []).length > 0 && (
                        <ul>
                          {(svc.content || []).map((line:string, li:number)=> <li key={li}>{line}</li>)}
                        </ul>
                      )}
                    </div>
                    <div className="ad-item__acts">
                      <button type="button" className="ad-btn ad-btn--sm" onClick={() => startEditService(si)}>
                        <Icon name="edit" />수정
                      </button>
                      <button type="button" className="ad-btn ad-btn--ghost ad-btn--sm is-danger"
                        onClick={() => {
                          if (window.confirm(`<${svc.title}> 서비스 항목을 삭제하시겠습니까?`)) {
                            setMainServiceList(mainServiceList.filter((_:any, i:number)=> i!==si));
                          }
                        }}>
                        <Icon name="trash" />삭제
                      </button>
                    </div>
                  </div>

                  {editingServiceIndex === si && editingService && (
                    <div className="ad-item__edit">
                      <div className="ad-field">
                        <label>서비스 제목</label>
                        <input
                          className="ad-input"
                          placeholder="서비스 제목"
                          value={editingService.title || ''}
                          onChange={(e) => setEditingService({ ...editingService, title: e.target.value })}
                        />
                      </div>

                      <div className="ad-field">
                        <label>서비스 내용</label>
                        <div>
                          {(editingService.content || []).map((line:string, li:number)=> (
                            <div key={li} className="ad-rep__line">
                              <input
                                className="ad-input"
                                placeholder="내용"
                                value={line}
                                onChange={(e) => {
                                  const newContent = (editingService.content || []).map((x:string, xi:number)=> xi===li ? e.target.value : x);
                                  setEditingService({ ...editingService, content: newContent });
                                }}
                              />
                              <button type="button" className="ad-btn ad-btn--ghost ad-btn--icon is-danger" aria-label="내용 삭제" style={{ height: 42 }}
                                onClick={() => {
                                  const newContent = (editingService.content || []).filter((_:string, xi:number)=> xi !== li);
                                  setEditingService({ ...editingService, content: newContent });
                                }}>
                                <Icon name="trash" />
                              </button>
                            </div>
                          ))}
                          <button type="button" className="ad-btn ad-btn--sm" style={{ marginTop: 4 }}
                            onClick={() => setEditingService({ ...editingService, content: [...(editingService.content || []), ''] })}>
                            <Icon name="plus" />항목 추가
                          </button>
                        </div>
                      </div>

                      {renderItemImage(
                        '이미지',
                        editingService.image,
                        () => deleteServerImage(editingService.image, () => setEditingService({ ...editingService, image: '' })),
                        serviceDropzone,
                        () => setCurrentServiceIndex(si),
                        serviceImageFiles[si] || [],
                        (fileIndex) => deleteServiceImage(si, fileIndex),
                      )}

                      <div className="ad-item__editfoot">
                        <button type="button" className="ad-btn" onClick={cancelEditService}>취소</button>
                        <button type="button" className="ad-btn ad-btn--primary" onClick={() => saveServiceItem(si)}>항목 저장</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <button type="button" className="ad-btn ad-btn--dashed"
                onClick={()=> {
                  const newService = { title: '', content: [''], image: '' };
                  setMainServiceList([ ...mainServiceList, newService ]);
                  // 새로 추가된 항목을 바로 수정 모드로 설정
                  const newIndex = mainServiceList.length;
                  setTimeout(() => {
                    startEditService(newIndex);
                  }, 100);
                }}>
                <Icon name="plus" />서비스 추가
              </button>
            </div>
          )}

          {/* 시설 사진 */}
          {currentTab === 3 && (
            <div className="ad-items">
              {facilityList.map((fc:any, fi:number)=> (
                <div key={fi} className={`ad-item ${editingFacilityIndex === fi ? 'is-editing' : ''}`}>
                  <div className="ad-item__row">
                    {fc.image
                      ? <img className="ad-thumb" src={`${MainURL}/images/notice/${fc.image}`} alt="" />
                      : <span className="ad-thumb ad-thumb--empty"><Icon name="image" /></span>}
                    <div className="ad-item__body">
                      <h3>{fc.title || '시설명 없음'}</h3>
                    </div>
                    <div className="ad-item__acts">
                      <button type="button" className="ad-btn ad-btn--sm" onClick={() => startEditFacility(fi)}>
                        <Icon name="edit" />수정
                      </button>
                      <button type="button" className="ad-btn ad-btn--ghost ad-btn--sm is-danger"
                        onClick={() => {
                          if (window.confirm(`<${fc.title || '시설'}> 항목을 삭제하시겠습니까?`)) {
                            setFacilityList(facilityList.filter((_:any, i:number)=> i!==fi));
                          }
                        }}>
                        <Icon name="trash" />삭제
                      </button>
                    </div>
                  </div>

                  {editingFacilityIndex === fi && editingFacility && (
                    <div className="ad-item__edit">
                      <div className="ad-field">
                        <label>시설명</label>
                        <input
                          className="ad-input"
                          placeholder="시설명"
                          value={editingFacility.title || ''}
                          onChange={(e) => setEditingFacility({ ...editingFacility, title: e.target.value })}
                        />
                      </div>

                      {renderItemImage(
                        '이미지',
                        editingFacility.image,
                        () => deleteServerImage(editingFacility.image, () => setEditingFacility({ ...editingFacility, image: '' })),
                        facilityDropzone,
                        () => setCurrentFacilityIndex(fi),
                        facilityImageFiles[fi] || [],
                        (fileIndex) => deleteFacilityImage(fi, fileIndex),
                      )}

                      <div className="ad-item__editfoot">
                        <button type="button" className="ad-btn" onClick={cancelEditFacility}>취소</button>
                        <button type="button" className="ad-btn ad-btn--primary" onClick={() => saveFacilityItem(fi)}>항목 저장</button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              <button type="button" className="ad-btn ad-btn--dashed"
                onClick={()=> {
                  const newItem = { title: '', image: '' };
                  setFacilityList([ ...facilityList, newItem ]);
                  const newIndex = facilityList.length;
                  setTimeout(() => { startEditFacility(newIndex); }, 100);
                }}>
                <Icon name="plus" />시설 추가
              </button>
            </div>
          )}

          {/* 시설 정보 */}
          {currentTab === 4 && (
            <>
              <div className="ad-grid2">
                {textField('이름', 'name')}
                {textField('센터장', 'charger')}
              </div>
              {textField('주소', 'address')}
              <div className="ad-grid2">
                {textField('문의', 'quiry')}
                {textField('후원계좌', 'supportAccount')}
              </div>
              <div className="ad-grid2">
                {textField('유튜브', 'youtube', 'https://')}
                {textField('블로그', 'blog', 'https://')}
                {textField('페이스북', 'facebook', 'https://')}
                {textField('인스타그램', 'instar', 'https://')}
              </div>
              {textField('메인 이미지 텍스트', 'imageText')}
            </>
          )}

          {/* 오시는 길 */}
          {currentTab === 5 && (
            <>
              {textField('네이버 지도', 'placeNaver', 'https://')}
              {textField('카카오 지도', 'placeKakao', 'https://')}
              {textField('주소', 'address')}
            </>
          )}
        </div>

        <div className="ad-card__foot">
          <span className="grow">모든 탭의 변경 사항은 저장 버튼을 눌러야 홈페이지에 반영됩니다.</span>
          <button type="button" className="ad-btn" onClick={()=>navigate('/admin/main')}>취소</button>
          <button type="button" className={`ad-btn ad-btn--primary ${saving ? 'is-disabled' : ''}`} onClick={save}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
      </div>
    </>
  );
}
